import React, { useState, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Camera, MessageSquare, X, Upload, Loader2, CheckCircle, AlertCircle, Eye, Trash2, Image as ImageIcon } from 'lucide-react';
import cloudinaryService from '../cloudinaryService';
import { useTranslation } from '../hooks/useTranslation';
import '../pwa-styles.css';

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}


// Image Preview Component
const ImagePreview = ({ image, onRemove, onView, t }) => (
  <div className="relative group">
    <img
      src={cloudinaryService.getThumbnailUrl(image.url, 150)}
      alt={image.alt}
      className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
      onClick={() => onView(image)}
    />
    <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
      <button
        onClick={() => onView(image)}
        className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
        title={t('checklist.view_full_size')}
      >
        <Eye className="h-4 w-4 text-gray-700" />
      </button>
      <button
        onClick={() => onRemove(image.id)}
        className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
        title={t('checklist.remove_image')}
      >
        <Trash2 className="h-4 w-4 text-white" />
      </button>
    </div>
    {image.uploadStatus === 'uploading' && (
      <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    )}
    {image.uploadStatus === 'error' && (
      <div className="absolute top-2 right-2">
        <AlertCircle className="h-5 w-5 text-red-500" />
      </div>
    )}
    {image.uploadStatus === 'success' && (
      <div className="absolute top-2 right-2">
        <CheckCircle className="h-5 w-5 text-green-500" />
      </div>
    )}
  </div>
);

// ChecklistPage Component
function ChecklistPage({
  className,
  onNext,
  onPrevious,
  onNavigate,
  checklistItems = [],
  onItemChange,
  categoryTitle = "DOCUMENTAZIONE",
  categoryTranslation = "Documentation",
  controlItemTitle = "Document collection pouch",
  controlItemTitleItalian = "Bustina raccolta documenti",
  currentItemIndex = 1,
  totalItems = 1,
  selectedForm,
  onOpenFormDetails,
  isNavigationDisabled = false, // Add this prop
  isChecklistComplete = false, // Add completion status prop
  // Progress props
  currentCategoryIndex = 1,
  totalCategories = 1,
  currentControlItemIndex = 1,
  totalControlItems = 1,
  // Score props
  currentScore = null,
  showScore = false,
  showSummaryButton = false, // NEW: Show Summary button only when audit is complete
}) {
  const { t, language } = useTranslation();
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tempComment, setTempComment] = useState('');
  const [uploadingItems, setUploadingItems] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [viewImageDialog, setViewImageDialog] = useState({ open: false, image: null });
  const [cloudinaryStatus, setCloudinaryStatus] = useState(cloudinaryService.isConfigured());
  const [imageOptionsDialog, setImageOptionsDialog] = useState({ open: false, itemId: null });
  const [cameraDialog, setCameraDialog] = useState({ open: false, itemId: null });
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, pendingItem: null });
  const videoRef = React.useRef(null);
  const uploadInProgressRef = useRef({}); // Track which items have uploads in progress

  // Image compression utility
  const compressImage = (file, targetSizeKB = 200, maxWidth = 1920, maxHeight = 1080) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // Start with quality 0.8 and reduce until under target size
        let quality = 0.8;
        let compressed;

        const compress = () => {
          compressed = canvas.toDataURL('image/jpeg', quality);
          const sizeKB = Math.round((compressed.length * 0.75) / 1024);

          if (sizeKB > targetSizeKB && quality > 0.1) {
            quality -= 0.1;
            compress();
          } else {
            // Convert base64 to File object
            fetch(compressed)
              .then(res => res.blob())
              .then(blob => {
                const compressedFile = new File([blob], file.name || 'camera-capture.jpg', {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              });
          }
        };

        compress();
      };

      img.src = typeof file === 'string' ? file : URL.createObjectURL(file);
    });
  };

  // Initialize camera
  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 }
        }
      });

      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      return true;
    } catch (error) {
      console.error('Camera initialization failed:', error);
      return false;
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Capture image from camera
  const captureImage = async () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageDataUrl);
    stopCamera();
  };

  // Handle image options dialog
  const openImageOptions = (itemId) => {
    setImageOptionsDialog({ open: true, itemId });
  };

  // Handle camera option
  const handleCameraOption = async (itemId) => {
    setImageOptionsDialog({ open: false, itemId: null });
    setCameraDialog({ open: true, itemId });

    // Small delay to ensure dialog is rendered
    setTimeout(async () => {
      await initializeCamera();
    }, 100);
  };

  // Handle upload option
  const handleUploadOption = (itemId) => {
    setImageOptionsDialog({ open: false, itemId: null });
    // Trigger file input
    document.getElementById(`image-upload-${itemId}`).click();
  };

  // Handle captured image save
  const handleSaveCapturedImage = async () => {
    if (!capturedImage || !cameraDialog.itemId) return;

    try {
      // Create a file from the captured image
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });

      // Compress the image
      const compressedFile = await compressImage(file);

      // Create a fake event object for handleImageUpload
      const fakeEvent = {
        target: {
          files: [compressedFile]
        }
      };

      // Use existing upload logic
      await handleImageUpload(cameraDialog.itemId, fakeEvent);

      // Clean up
      setCapturedImage(null);
      setCameraDialog({ open: false, itemId: null });
    } catch (error) {
      console.error('Error saving captured image:', error);
    }
  };

  // Clean up camera on dialog close
  const handleCameraDialogClose = () => {
    stopCamera();
    setCapturedImage(null);
    setCameraDialog({ open: false, itemId: null });
  };

  const handleItemChange = (updatedItem, fromUpload = false) => {
    console.log(`🔥 CHECKLIST PAGE - handleItemChange called:`, {
      itemId: updatedItem.id,
      isChecked: updatedItem.isChecked,
      fromUpload,
      hasOnItemChange: typeof onItemChange === 'function'
    });

    // Prevent state changes while upload is in progress (except when called from upload itself)
    if (uploadInProgressRef.current[updatedItem.id] && !fromUpload) {
      console.warn(`Upload in progress for item ${updatedItem.id}, deferring state change`);
      return;
    }

    // If unchecking an item, clear its images and comments
    if (!updatedItem.isChecked) {
      updatedItem = {
        ...updatedItem,
        images: [],
        comment: '' // Use 'comment' (singular)
      };
    }

    // Implement mutual exclusivity logic with confirmation dialogs
    if (updatedItem.isChecked) {
      const isNoProblemsDetected = updatedItem.deductionPoints === 0;

      if (isNoProblemsDetected) {
        // Check if any problem items are currently checked
        const checkedProblemItems = checklistItems.filter(item => item.deductionPoints > 0 && item.isChecked);
        if (checkedProblemItems.length > 0) {
          // Show confirmation before clearing problem selections
          setConfirmDialog({ open: true, type: 'clearProblems', pendingItem: updatedItem });
          return; // Don't apply yet — wait for confirmation
        }
      } else {
        // Check if any "no problems" items are currently checked
        const checkedNoProblemsItems = checklistItems.filter(item => item.deductionPoints === 0 && item.isChecked);
        if (checkedNoProblemsItems.length > 0) {
          // Show confirmation before clearing "no problems" selection
          setConfirmDialog({ open: true, type: 'clearNoProblem', pendingItem: updatedItem });
          return; // Don't apply yet — wait for confirmation
        }
      }
    }

    // Apply the main change (no conflict scenario)
    if (onItemChange) {
      onItemChange(updatedItem);
    }
  };

  // Handle confirmation dialog result
  const handleConfirmExclusivity = () => {
    const { type, pendingItem } = confirmDialog;

    if (type === 'clearProblems') {
      // User confirmed: clear all problem items and apply "no problems detected"
      const updatedItems = checklistItems.map(item => ({
        ...item,
        isChecked: item.id === pendingItem.id ? true : (item.deductionPoints === 0 ? item.isChecked : false),
        images: item.id === pendingItem.id ? item.images : [],
        comment: item.id === pendingItem.id ? item.comment : ''
      }));
      updatedItems.forEach(item => {
        if (item.id !== pendingItem.id && item.isChecked !== (checklistItems.find(orig => orig.id === item.id)?.isChecked || false)) {
          if (onItemChange) onItemChange(item);
        }
      });
      if (onItemChange) onItemChange(pendingItem);
    } else if (type === 'clearNoProblem') {
      // User confirmed: clear "no problems" items and apply the problem item
      const noProblemsItems = checklistItems.filter(item => item.deductionPoints === 0 && item.isChecked);
      noProblemsItems.forEach(noProblemItem => {
        if (onItemChange) onItemChange({ ...noProblemItem, isChecked: false, images: [], comment: '' });
      });
      if (onItemChange) onItemChange(pendingItem);
    }

    setConfirmDialog({ open: false, type: null, pendingItem: null });
  };

  const handleCancelExclusivity = () => {
    setConfirmDialog({ open: false, type: null, pendingItem: null });
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    }
  };

  // Enhanced image upload with Cloudinary
  const handleImageUpload = async (itemId, event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Prevent concurrent uploads for the same item
    if (uploadInProgressRef.current[itemId]) {
      console.warn(`Upload already in progress for item ${itemId}`);
      return;
    }

    // Mark upload as in progress
    uploadInProgressRef.current[itemId] = true;

    // Check if Cloudinary is configured - show gentle warning, not alert
    if (!cloudinaryService.isConfigured()) {
      console.error('Cloudinary is not configured. Images will be stored locally.');
      // Create local-only images without uploading to Cloudinary
      const item = checklistItems.find(i => i.id === itemId);
      if (!item) {
        uploadInProgressRef.current[itemId] = false;
        return;
      }

      // Compress images even for local storage
      const compressedImages = await Promise.all(
        Array.from(files).map(async (file) => {
          const compressedFile = await compressImage(file);
          return {
            id: Math.random().toString(36).substr(2, 9),
            file: compressedFile,
            url: URL.createObjectURL(compressedFile),
            alt: `Image for ${item.label}`,
            uploadStatus: 'local',
            localFile: true
          };
        })
      );

      handleItemChange({ ...item, images: [...(item.images || []), ...compressedImages] }, true); // Pass true for upload
      uploadInProgressRef.current[itemId] = false;
      return;
    }

    const item = checklistItems.find(i => i.id === itemId);
    if (!item) {
      uploadInProgressRef.current[itemId] = false;
      return;
    }

    // Set uploading state
    setUploadingItems(prev => ({ ...prev, [itemId]: true }));
    setUploadProgress(prev => ({ ...prev, [itemId]: 0 }));

    const newImages = [];
    const totalFiles = files.length;

    for (let i = 0; i < totalFiles; i++) {
      const file = files[i];

      // Validate file type
      const allowedFormats = (process.env.REACT_APP_ALLOWED_IMAGE_FORMATS || 'jpg,jpeg,png,gif,webp').split(',');
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!allowedFormats.includes(fileExtension)) {
        alert(`File ${file.name} has an invalid format. Allowed formats: ${allowedFormats.join(', ')}`);
        continue;
      }

      // Compress image before upload
      const compressedFile = await compressImage(file);
      console.log(`📸 Image compressed: ${file.name} - Original: ${Math.round(file.size/1024)}KB -> Compressed: ${Math.round(compressedFile.size/1024)}KB`);

      // Create temporary image object with local preview
      const tempImage = {
        id: Math.random().toString(36).substr(2, 9),
        file: compressedFile,
        url: URL.createObjectURL(compressedFile),
        alt: `Image for ${item.label}`,
        uploadStatus: 'uploading',
        localFile: true
      };

      newImages.push(tempImage);

      // Update progress
      setUploadProgress(prev => ({
        ...prev,
        [itemId]: Math.round(((i + 1) / totalFiles) * 100)
      }));

      // Upload to Cloudinary
      try {
        const uploadOptions = {
          folder: `${process.env.REACT_APP_CLOUDINARY_UPLOAD_FOLDER || 'audit-images'}/${selectedForm?.id || 'general'}`,
          tags: [
            `audit_${selectedForm?.id}`,
            `category_${categoryTitle}`,
            `control_${controlItemTitle}`,
            `item_${itemId}`
          ],
          context: `item=${item.label}|category=${categoryTitle}`
          // NOTE: 'eager' transformations not allowed for unsigned uploads
          // Thumbnails will be generated on-the-fly via URL transformation
        };

        const result = await cloudinaryService.uploadImage(compressedFile, uploadOptions);

        if (result.success) {
          // Update image with Cloudinary URL
          tempImage.url = result.data.url;
          tempImage.thumbnailUrl = result.data.thumbnailUrl || cloudinaryService.getThumbnailUrl(result.data.url, 200);
          tempImage.cloudinaryId = result.data.id;
          tempImage.publicId = result.data.publicId;
          tempImage.uploadStatus = 'success';
          tempImage.localFile = false;
          tempImage.cloudinaryData = result.data;

          // Clean up local object URL
          if (tempImage.url.startsWith('blob:')) {
            URL.revokeObjectURL(tempImage.url);
          }

          console.log(`✅ Image uploaded successfully: ${file.name}`);
        } else {
          tempImage.uploadStatus = 'error';
          tempImage.error = result.error;
          console.error(`❌ Failed to upload ${file.name}:`, result.error);
        }
      } catch (error) {
        tempImage.uploadStatus = 'error';
        tempImage.error = error.message;
        console.error(`❌ Error uploading ${file.name}:`, error);
      }
    }

    // Update item with new images
    const updatedImages = [...(item.images || []), ...newImages];
    handleItemChange({ ...item, images: updatedImages }, true); // Pass true to indicate this is from upload

    // Clear uploading state
    setUploadingItems(prev => ({ ...prev, [itemId]: false }));
    setUploadProgress(prev => ({ ...prev, [itemId]: 0 }));
    uploadInProgressRef.current[itemId] = false; // Clear upload in progress flag

    // Show success message
    const successCount = newImages.filter(img => img.uploadStatus === 'success').length;
    const errorCount = newImages.filter(img => img.uploadStatus === 'error').length;

    if (successCount > 0 && errorCount === 0) {
      console.log(`✅ All ${successCount} image(s) uploaded successfully`);
    } else if (successCount > 0 && errorCount > 0) {
      console.log(`⚠️ ${successCount} image(s) uploaded, ${errorCount} failed`);
    } else if (errorCount > 0) {
      console.log(`❌ Failed to upload ${errorCount} image(s)`);
    }
  };

  const openImageDialog = (item) => {
    setSelectedItem(item);
    setImageDialogOpen(true);
  };

  const openCommentDialog = (item) => {
    setSelectedItem(item);
    setTempComment(item.comment || '');
    setCommentDialogOpen(true);
  };

  const saveComment = () => {
    if (selectedItem) {
      handleItemChange({ ...selectedItem, comment: tempComment });
    }
    setCommentDialogOpen(false);
    setSelectedItem(null);
    setTempComment('');
  };

  const removeImage = (itemId, imageId) => {
    const item = checklistItems.find(i => i.id === itemId);
    if (!item) return;
    
    const imageToRemove = item.images?.find(img => img.id === imageId);
    if (imageToRemove?.localFile && imageToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.url);
    }
    
    const updatedImages = (item.images || []).filter(img => img.id !== imageId);
    handleItemChange({ ...item, images: updatedImages });
  };

  const viewFullImage = (image) => {
    setViewImageDialog({ open: true, image });
  };

  // Check Cloudinary configuration on mount - ONLY LOG, NO ALERTS
  React.useEffect(() => {
    const configured = cloudinaryService.isConfigured();
    setCloudinaryStatus(configured);

    if (!configured) {
      console.warn('⚠️ Cloudinary is not configured. Please add the following to your .env.local file:');
      console.warn('REACT_APP_CLOUDINARY_CLOUD_NAME=your_cloud_name');
      console.warn('REACT_APP_CLOUDINARY_UPLOAD_PRESET=your_upload_preset');
    } else {
      console.log('✅ Cloudinary is configured and ready');
    }
  }, []); // Empty array - runs only once

  // Cleanup camera on component unmount
  React.useEffect(() => {
    return () => {
      stopCamera();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn("pwa-flex-page bg-background", className)}>
      <div className="pwa-tablet-container container mx-auto max-w-7xl flex flex-col h-full">
        <header className="pwa-fixed-header flex-shrink-0 bg-background px-4 md:px-6 pb-4 border-b border-gray-200 opacity-0 animate-[fadeInDown_0.5s_ease-out_forwards]">
          <div className="space-y-3 mt-6">
            {/* Category Badge */}
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-600">
                {t('checklist.category')} – {language === 'it' ? categoryTranslation : categoryTitle}
              </Badge>
              {!cloudinaryStatus && (
                <Badge variant="destructive" className="animate-pulse">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {t('checklist.image_uploads_disabled')}
                </Badge>
              )}
            </div>

            {/* Navigation Buttons + Score on same row */}
            <div className="flex items-center justify-between gap-3 mt-8">
              <div className="flex items-center gap-3">
                <Button onClick={() => onNavigate('controlItemsView')} variant="outline" size="sm">
                  {t('checklist.control_items')}
                </Button>
                <Button onClick={() => onNavigate('categoryView')} variant="outline" size="sm">
                  {t('checklist.categories')}
                </Button>
                <Button onClick={() => {
                  console.log('🔥 ChecklistPage - Summary button clicked!', { onNavigate: !!onNavigate });
                  if (onNavigate) {
                    console.log('🔥 Calling onNavigate with auditSummary');
                    onNavigate('auditSummary');
                  } else {
                    console.error('❌ onNavigate is not defined!');
                  }
                }} variant="outline" size="sm">
                  {t('nav.summary')}
                </Button>
                <Button onClick={() => onNavigate('chillerAuditDashboard')} variant="outline" size="sm">
                  {t('checklist.dashboard')}
                </Button>
              </div>

              {/* Score Display - Aligned right on same row */}
              {showScore && currentScore && (
                <div className="bg-gradient-to-r mt-2 from-blue-50 to-blue-100 px-4 rounded-full border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-blue-600 font-medium">{t('checklist.score')}:</span>
                    <span className="font-bold text-blue-800 text-lg">{currentScore.achieved}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Control Item Title */}
            <div className="space-y-1">
              <h1 className="text-base md:text-lg lg:text-xl font-light text-foreground leading-snug">
                  {controlItemTitleItalian}

              </h1>
              {controlItemTitleItalian && (
                <p className="text-sm md:text-base lg:text-lg text-muted-foreground italic">
                {controlItemTitle}

                </p>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 opacity-0 animate-[slideUp_0.5s_ease-out_0.2s_forwards]" style={{ paddingBottom: '140px' }}>
          <Card className="w-full shadow-lg border-0 bg-card rounded-2xl overflow-hidden">
            <CardContent className="p-0">
                <table className="w-full border-separate border-spacing-0">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="bg-muted/30">
                      <th className="py-4 px-6 text-left text-xs font-semibold text-muted-foreground uppercase w-8">
                        <span className="sr-only">Checkbox</span>
                      </th>
                      <th className="py-4 px-6 text-left text-xs font-semibold text-muted-foreground uppercase min-w-0 flex-1">
                        {t('checklist.check_description')}
                      </th>
                      <th className="py-4 px-6 text-center text-xs font-semibold text-muted-foreground uppercase w-24">
                        {t('checklist.quantity')}
                      </th>
                      <th className="py-4 px-6 text-center text-xs font-semibold text-muted-foreground uppercase w-32">
                        {t('checklist.pictures')}
                      </th>
                      <th className="py-4 px-6 text-center text-xs font-semibold text-muted-foreground uppercase w-24">
                        {t('checklist.comment')}
                      </th>
                      <th className="py-4 px-6 text-center text-xs font-semibold text-muted-foreground uppercase w-32">
                        {t('checklist.production_phase')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklistItems.map((item, index) => (
                      <tr
                        key={item.id}
                        className={cn(
                          index === checklistItems.length - 1 ? "border-b-0" : "border-b border-border hover:bg-muted/30 transition-colors",
                          "opacity-0 animate-[slideInRow_0.3s_ease-out_forwards]"
                        )}
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <td className="py-4 px-6 w-8">
                          <input
                            type="checkbox"
                            checked={item.isChecked || false}
                            onChange={e => {
                              console.log(`🔥 CHECKBOX CLICKED - Item ${item.id}, checked: ${e.target.checked}`);
                              handleItemChange({ ...item, isChecked: e.target.checked });
                            }}
                            className="accent-primary h-4 w-4 rounded border-muted-foreground/20 focus:ring-2 focus:ring-primary"
                          />
                        </td>
                        <td className="py-4 px-6 min-w-0 flex-1">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-foreground leading-relaxed">
                                {item.labelItalian}
                            </p>
                            {item.labelItalian && (
                              <p className="text-xs text-muted-foreground italic">
                              {item.label}

                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6 w-24">
                          {item.requiresQuantity ? (
                            <input
                              type="number"
                              value={item.quantity || 0}
                              min={0}
                              onChange={e => handleItemChange({ ...item, quantity: parseInt(e.target.value) || 0 })}
                              className="h-8 w-16 text-center border border-muted-foreground/20 rounded focus:border-primary focus:ring-2 focus:ring-primary/20"
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('msg.not_available')}</span>
                          )}
                        </td>
                        <td className="py-4 px-6 w-32">
                          {item.requiresAttachment ? (
                            <div className="flex items-center justify-center gap-2">
                              <input
                                type="file"
                                id={`image-upload-${item.id}`}
                                multiple
                                accept="image/*"
                                onChange={e => handleImageUpload(item.id, e)}
                                className="hidden"
                                disabled={!cloudinaryStatus || uploadingItems[item.id]}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-10 w-10 p-0 hover:bg-primary/10"
                                onClick={() => openImageOptions(item.id)}
                                disabled={uploadingItems[item.id]}
                              >
                                {uploadingItems[item.id] ? (
                                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                ) : (
                                  <ImageIcon className="h-6 w-6 text-muted-foreground hover:text-primary transition-colors" />
                                )}
                              </Button>
                              {item.images && item.images.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-10 w-10 p-0 hover:bg-primary/10"
                                  onClick={() => openImageDialog(item)}
                                >
                                  <span className="text-sm font-bold text-primary">{item.images.length}</span>
                                </Button>
                              )}
                              {uploadProgress[item.id] > 0 && uploadProgress[item.id] < 100 && (
                                <span className="text-xs text-blue-600">{uploadProgress[item.id]}%</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('msg.not_available')}</span>
                          )}
                        </td>
                        <td className="py-4 px-6 w-24">
                          {item.requiresComment ? (
                            <div className="flex items-center justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-10 w-10 p-0 hover:bg-primary/10"
                                onClick={() => openCommentDialog(item)}
                              >
                                <MessageSquare className={cn(
                                  "h-6 w-6 transition-colors",
                                  item.comment ? "text-primary" : "text-muted-foreground hover:text-primary"
                                )} />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('msg.not_available')}</span>
                          )}
                        </td>
                        <td className="py-4 px-6 w-32">
                          {item.requiresProductionPhase ? (
                            <select
                              value={item.productionPhase || t('checklist.phase')}
                              onChange={e => handleItemChange({ ...item, productionPhase: e.target.value })}
                              className="h-8 w-full border border-muted-foreground/20 rounded text-xs focus:border-primary focus:ring-2 focus:ring-primary/20"
                            >
                              <option value={t('checklist.phase')}>{t('checklist.phase')}</option>
                              <option value={t('checklist.phase_planning')}>{t('checklist.phase_planning')}</option>
                              <option value={t('checklist.phase_development')}>{t('checklist.phase_development')}</option>
                              <option value={t('checklist.phase_testing')}>{t('checklist.phase_testing')}</option>
                              <option value={t('checklist.phase_production')}>{t('checklist.phase_production')}</option>
                              <option value={t('checklist.phase_complete')}>{t('checklist.phase_complete')}</option>
                            </select>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('msg.not_available')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    
                    {/* Empty state */}
                    {checklistItems.length === 0 && (
                      <tr>
                        <td colSpan="6" className="py-12 text-center">
                          <div className="text-muted-foreground">
                            <p className="text-lg font-medium mb-2">{t('checklist.no_check_items')}</p>
                            <p className="text-sm">{t('checklist.no_check_items_desc')}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Fixed Footer Section with Progress */}
      <footer className="pwa-fixed-footer flex-shrink-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="container mx-auto max-w-7xl">
          <nav className="w-full px-4 md:px-6 py-4 pb-5">
            <div className="flex flex-col md:flex-row gap-3 md:gap-6 items-stretch md:items-center justify-between">
              <Button
                onClick={onPrevious}
                className="w-full md:w-auto text-lg font-semibold rounded-full px-8 py-4 md:py-3 md:px-10"
                size="lg"
                variant="secondary"
                disabled={isNavigationDisabled}
              >
                {isNavigationDisabled ? t('checklist.loading') : t('checklist.previous')}
              </Button>

              {/* Progress widget */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 min-w-[200px]">
                <div className="text-center space-y-1">
                  <div className="text-xs font-medium text-gray-600">
                    {t('checklist.category_of').replace('{0}', currentCategoryIndex).replace('{1}', totalCategories)}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {language === 'it' ? categoryTranslation : categoryTitle}
                  </div>
                  <div className="text-xs text-blue-600">
                    {t('checklist.control_item_of').replace('{0}', currentControlItemIndex).replace('{1}', totalControlItems)}
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div 
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${(currentControlItemIndex / totalControlItems) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={handleNext}
                className={cn(
                  "w-full md:w-auto text-lg font-semibold rounded-full px-8 py-4 md:py-3 md:px-10",
                  !isChecklistComplete && !isNavigationDisabled
                    ? "bg-gray-400 hover:bg-gray-500 cursor-not-allowed"
                    : ""
                )}
                size="lg"
                disabled={isNavigationDisabled || !isChecklistComplete}
                title={!isChecklistComplete ? t('checklist.complete_tooltip') : ""}
              >
                {isNavigationDisabled
                  ? t('checklist.saving')
                  : !isChecklistComplete
                    ? t('checklist.complete_required')
                    : t('checklist.next')
                }
              </Button>
            </div>
          </nav>
        </div>
      </footer>

      {/* Enhanced Image Gallery Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden">
          <DialogHeader className="pb-3">
            <DialogTitle className="flex items-center justify-between">
              <span>{t('checklist.images_for').replace('{0}', selectedItem?.label || '')}</span>
              {selectedItem?.images?.length > 0 && (
                <Badge variant="secondary">{t('checklist.images_count').replace('{0}', selectedItem.images.length)}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[calc(85vh-100px)]">
            {selectedItem?.images?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedItem.images.map(image => (
                  <ImagePreview
                    key={image.id}
                    image={image}
                    onRemove={(imageId) => removeImage(selectedItem.id, imageId)}
                    onView={viewFullImage}
                    t={t}
                  />
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">{t('checklist.no_images')}</p>
                <p className="text-sm text-gray-400">{t('checklist.use_camera')}</p>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-3 border-t">
            <div className="text-sm text-gray-500">
              {cloudinaryStatus ? (
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t('checklist.cloud_storage')}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  {t('checklist.cloud_not_configured')}
                </span>
              )}
            </div>
            <label htmlFor={`gallery-upload-${selectedItem?.id}`}>
              <input
                type="file"
                id={`gallery-upload-${selectedItem?.id}`}
                multiple
                accept="image/*"
                onChange={e => selectedItem && handleImageUpload(selectedItem.id, e)}
                className="hidden"
                disabled={!cloudinaryStatus}
              />
              <Button variant="outline" size="sm" asChild disabled={!cloudinaryStatus}>
                <span className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  {t('checklist.add_more_images')}
                </span>
              </Button>
            </label>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Image View Dialog */}
      <Dialog open={viewImageDialog.open} onOpenChange={(open) => setViewImageDialog({ open, image: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          {viewImageDialog.image && (
            <div className="relative">
              <img 
                src={viewImageDialog.image.url} 
                alt={viewImageDialog.image.alt} 
                className="w-full h-auto max-h-[90vh] object-contain"
              />
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
                onClick={() => setViewImageDialog({ open: false, image: null })}
              >
                <X className="h-4 w-4" />
              </Button>
              {viewImageDialog.image.cloudinaryData && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-3 text-xs">
                  <div className="flex justify-between items-center">
                    <span>{viewImageDialog.image.cloudinaryData.originalFilename}</span>
                    <span>{Math.round(viewImageDialog.image.cloudinaryData.bytes / 1024)} KB</span>
                    <span>{viewImageDialog.image.cloudinaryData.width} × {viewImageDialog.image.cloudinaryData.height}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('checklist.comment_for').replace('{0}', selectedItem?.label || '')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Textarea
              value={tempComment}
              onChange={e => setTempComment(e.target.value)}
              placeholder={t('checklist.enter_comment')}
              className="min-h-[100px] resize-none"
            />
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setTempComment('')} className="text-red-600 hover:text-red-700">
                {t('checklist.clear')}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setCommentDialogOpen(false); setTempComment(''); }}>
                  {t('checklist.cancel')}
                </Button>
                <Button onClick={saveComment}>{t('checklist.save_comment')}</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Options Dialog */}
      <Dialog open={imageOptionsDialog.open} onOpenChange={(open) => setImageOptionsDialog({ open, itemId: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('checklist.add_image')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">{t('checklist.choose_method')}</p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="h-24 flex flex-col gap-2"
                onClick={() => handleCameraOption(imageOptionsDialog.itemId)}
              >
                <Camera className="h-8 w-8" />
                <span>{t('checklist.take_photo')}</span>
              </Button>
              <Button
                variant="outline"
                className="h-24 flex flex-col gap-2"
                onClick={() => handleUploadOption(imageOptionsDialog.itemId)}
              >
                <Upload className="h-8 w-8" />
                <span>{t('checklist.upload_file')}</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera Capture Dialog */}
      <Dialog open={cameraDialog.open} onOpenChange={handleCameraDialogClose}>
        <DialogContent className="w-[min(70vw,800px)] h-[min(70vh,600px)] max-w-[800px] max-h-[600px] p-0">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>{t('checklist.take_photo')}</DialogTitle>
          </DialogHeader>
          <div className="p-4 h-full flex flex-col">
            {!capturedImage ? (
              <div className="flex-1 flex flex-col space-y-4">
                <div className="relative bg-black rounded-lg overflow-hidden flex-1 min-h-0">
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
                    <Button
                      onClick={captureImage}
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-20 h-20 shadow-2xl border-4 border-white/20 transition-all duration-200 hover:scale-105"
                    >
                      <Camera className="h-8 w-8" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-center text-gray-600 py-2">
                  {t('checklist.position_camera')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative rounded-lg overflow-hidden">
                  <img
                    src={capturedImage}
                    alt="Captured"
                    className="w-full h-auto max-h-96 object-contain"
                  />
                </div>
                <div className="flex justify-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCapturedImage(null);
                      initializeCamera();
                    }}
                  >
                    {t('checklist.retake')}
                  </Button>
                  <Button onClick={handleSaveCapturedImage}>
                    {t('checklist.save_image')}
                  </Button>
                </div>
                <p className="text-sm text-center text-gray-600">
                  {t('checklist.compress_note')}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mutual Exclusivity Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => { if (!open) handleCancelExclusivity(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {t('checklist.confirm_title') || 'Are you sure?'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-gray-600">
              {confirmDialog.type === 'clearProblems'
                ? (t('checklist.confirm_clear_problems') || 'Selecting "No Problem Detected" will clear all your selected problem items and their attached images/comments.')
                : (t('checklist.confirm_clear_no_problem') || 'Selecting a problem item will clear the "No Problem Detected" selection.')
              }
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancelExclusivity}>
                {t('checklist.cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleConfirmExclusivity}
                className={confirmDialog.type === 'clearProblems' ? 'bg-amber-500 hover:bg-amber-600' : ''}
              >
                {t('checklist.confirm_yes') || 'Yes, continue'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRow {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

export default ChecklistPage;